import { useTranslations } from "@jugl-web/utils";
import { useCallback, useState } from "react";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import {
  saveItemToStorage,
  NEW_ENTITY_SESSION_PARAMS,
} from "@jugl-web/utils/storage";
import { useParams, useSearchParams } from "react-router-dom";
import { SignUpFlowLayout } from "../SignUpFlowLayout";
import {
  CreateEntityForm,
  EntityFormProps,
} from "./components/CreateEntityForm/CreateEntityForm";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";
import { AuthDiscardConfirmationAlert } from "../../components/AuthDiscardConfirmationAlert/AuthDiscardConfirmationAlert";
import { SelectProductStep } from "./components/SelectProductStep";

type Step = "selectProduct" | "createWorkspace";

export const SignUpNewWorkspace = () => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const [searchParams] = useSearchParams();
  const skipSelectProduct = searchParams.get("skipSelectProduct") === "true";
  const [step, setStep] = useState<Step>(
    skipSelectProduct ? "createWorkspace" : "selectProduct"
  );
  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState(false);
  const { flow } = useParams();
  const isInAppFlow = flow === "inapp";

  const handleSubmit: EntityFormProps["onSubmit"] = useCallback(
    (values) => {
      saveItemToStorage(
        NEW_ENTITY_SESSION_PARAMS,
        {
          country: values.country?.iso,
          name: values.name,
          email: values.email,
          imageFile: values.imageFile,
        },
        sessionStorage
      );
      navigateToPage("authWorkspaceSetup", {
        flow: isInAppFlow ? "inapp" : "signup",
      });
    },
    [isInAppFlow, navigateToPage]
  );

  if (step === "selectProduct") {
    return (
      <SignUpFlowLayout
        type="newWorkspace"
        progressBar={50}
        footerClassName="pb-6"
        onBackClick={() => setIsDiscardAlertOpen(true)}
      >
        <AuthDiscardConfirmationAlert
          isOpen={Boolean(isDiscardAlertOpen)}
          variant="workspace"
          onConfirm={() => {
            sessionStorage.removeItem(NEW_ENTITY_SESSION_PARAMS);
            if (isInAppFlow) {
              navigateToPage("entryPoint");
            } else {
              navigateToPage("authSignUpPurpose");
            }
          }}
          onClose={() => setIsDiscardAlertOpen(false)}
        />
        <SelectProductStep
          onSelectCX={() =>
            navigateToPage("cxOnboarding", undefined, {
              queryParams: { flow: "org-create" },
            })
          }
          onSelectPM={() => setStep("createWorkspace")}
        />
      </SignUpFlowLayout>
    );
  }

  return (
    <SignUpFlowLayout
      type="newWorkspace"
      progressBar={62}
      footerClassName="pb-6"
      onBackClick={() => {
        if (skipSelectProduct) {
          setIsDiscardAlertOpen(true);
        } else {
          setStep("selectProduct");
        }
      }}
    >
      {skipSelectProduct && (
        <AuthDiscardConfirmationAlert
          isOpen={Boolean(isDiscardAlertOpen)}
          variant="workspace"
          onConfirm={() => {
            sessionStorage.removeItem(NEW_ENTITY_SESSION_PARAMS);
            navigateToPage("entryPoint");
          }}
          onClose={() => setIsDiscardAlertOpen(false)}
        />
      )}
      <SignUpFlowContentLayout
        titleSectionClassName="w-full"
        title={
          <div className="pt-[24px] 2xl:pt-[80px]">
            {t({
              id: "create-entity.title",
              defaultMessage: "Let’s Build Your New HQ 🎉",
            })}
          </div>
        }
        subtitle={t({
          id: "create-entity.subtitle",
          defaultMessage:
            "Just a few quick details and we’ll roll out the red carpet",
        })}
        description={
          <div className="relative flex w-full max-w-[514px] flex-col items-center gap-4 2xl:pb-[64px]">
            <CreateEntityForm onSubmit={handleSubmit} />
          </div>
        }
      />
    </SignUpFlowLayout>
  );
};
